import React, { useState, useEffect, useRef } from 'react';
import { Mail, KeyRound, Loader2, ArrowRight, CheckCircle, XCircle, FileText, ChevronLeft, HelpCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '/api';
const fmt = (n) => Math.round(n).toLocaleString('sv-SE');

// Tooltip-komponent
function Tooltip({ text }) {
  const [show, setShow] = useState(false);
  return (
    <span className="relative inline-block ml-1">
      <button type="button" onClick={() => setShow(!show)} onBlur={() => setTimeout(() => setShow(false), 150)}
        className="text-slate-300 hover:text-blue-400 transition-colors align-middle">
        <HelpCircle size={14} />
      </button>
      {show && (
        <div className="absolute z-50 bottom-6 left-1/2 -translate-x-1/2 bg-slate-800 text-white text-xs rounded-xl px-3 py-2 w-48 shadow-xl">
          {text}
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
        </div>
      )}
    </span>
  );
}

function QuoteView({ quote, onBack, onRespond }) {
  const [responding, setResponding] = useState(false);
  const [done, setDone] = useState(quote.status === 'accepted' || quote.status === 'declined');
  const [currentStatus, setCurrentStatus] = useState(quote.status);
  const [comment, setComment] = useState('');
  const [pendingResponse, setPendingResponse] = useState(null); // 'accepted' | 'declined'

  // Beräkna giltighetsdatum
  const createdDate = new Date(quote.created_at);
  const validDays = quote.validity_days || 30;
  const expiryDate = new Date(createdDate);
  expiryDate.setDate(expiryDate.getDate() + validDays);
  const isExpired = expiryDate < new Date();

  const handleRespond = async (resp) => {
    setResponding(true);
    try {
      const res = await fetch(`${API_URL}/portal/quotes/token/${quote.portal_token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: resp, comment: comment.trim() || null })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setCurrentStatus(resp);
      setDone(true);
      onRespond(resp);
    } catch (e) { alert(e.message); }
    finally { setResponding(false); setPendingResponse(null); }
  };

  const alreadyAccepted = currentStatus === 'accepted';
  const alreadyDeclined = currentStatus === 'declined';

  return (
    <div className="space-y-6">
      {onBack && (
        <button onClick={onBack} className="flex items-center gap-2 text-slate-400 text-sm hover:text-slate-600">
          <ChevronLeft size={16} /> Tillbaka till mina offerter
        </button>
      )}

      {/* Offertdokument */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-slate-100 overflow-hidden max-w-[700px] mx-auto">
        <div className="p-4 sm:p-8 pb-0">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-6 gap-4">
            <div className="flex-1 min-w-0">
              {quote.logo
                ? <img src={quote.logo} alt="Logo" className="h-10 object-contain mb-3" />
                : <div className="text-xl font-black mb-2">{quote.company_name}</div>
              }
              <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-1">Leverantör</div>
              <div className="text-xs text-slate-500 leading-5">
                {quote.address && <div>{quote.address}</div>}
                {(quote.zip || quote.city) && <div>{quote.zip} {quote.city}</div>}
                {quote.org_nr && <div>Org.nr: {quote.org_nr}</div>}
                {quote.phone && <div>{quote.phone}</div>}
                {quote.company_email && <div className="break-all">{quote.company_email}</div>}
              </div>
            </div>
            <div className="sm:text-right flex sm:flex-col flex-row sm:gap-0 gap-4 items-start sm:items-end">
              <div className="hidden sm:block text-5xl font-black text-slate-100 leading-none mb-3">OFFERT</div>
              <div>
                <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Datum</div>
                <div className="font-bold mb-2 text-sm">{new Date(quote.created_at).toLocaleDateString('sv-SE')}</div>
              </div>
              {quote.quote_number && (
                <div>
                  <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest">Offertnummer</div>
                  <div className="font-bold text-sm">{quote.quote_number}</div>
                </div>
              )}
            </div>
          </div>

          {/* Mottagare */}
          <div className="border-y border-slate-100 py-4 mb-5">
            <div className="text-[9px] font-black uppercase text-blue-600 tracking-widest mb-2">Mottagare</div>
            <div className="text-xl font-black">{quote.customer_name || '—'}</div>
          </div>

          {/* Beskrivning */}
          {quote.description && (
            <div className="mb-6">
              <div className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-2">Beskrivning</div>
              <div className="text-sm italic text-slate-600 bg-slate-50 p-4 rounded-xl border-l-4 border-slate-200 whitespace-pre-wrap leading-relaxed">{quote.description}</div>
            </div>
          )}

          {/* Giltighetstid */}
          <div className="mb-6 flex items-center gap-2">
            <div className={`text-xs px-3 py-1.5 rounded-full font-bold ${isExpired ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-700'}`}>
              {isExpired
                ? `⚠️ Offerten gick ut ${expiryDate.toLocaleDateString('sv-SE')}`
                : `✓ Giltig till ${expiryDate.toLocaleDateString('sv-SE')} (${validDays} dagar)`
              }
            </div>
          </div>

          {/* Rader */}
          <div className="overflow-x-auto mb-6 -mx-4 sm:mx-0">
            <table className="w-full border-collapse min-w-[320px] px-4 sm:px-0">
              <thead>
                <tr className="border-b-2 border-slate-900">
                  <th className="py-2 sm:py-3 text-left text-[9px] sm:text-[10px] font-black uppercase tracking-wider pr-2">Beskrivning</th>
                  <th className="py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2">Antal</th>
                  <th className="py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-black uppercase tracking-wider px-2 hidden sm:table-cell">Pris/st</th>
                  <th className="py-2 sm:py-3 text-right text-[9px] sm:text-[10px] font-black uppercase tracking-wider pl-2">Belopp</th>
                </tr>
              </thead>
              <tbody>
                {quote.items.filter(i => i.description).map((item, idx) => (
                  <tr key={idx} className="border-b border-slate-100">
                    <td className="py-2 sm:py-3 text-xs sm:text-sm pr-2">{item.description}</td>
                    <td className="py-2 sm:py-3 text-right text-xs sm:text-sm px-2">{item.quantity}</td>
                    <td className="py-2 sm:py-3 text-right text-xs sm:text-sm px-2 hidden sm:table-cell">{fmt(item.price)} kr</td>
                    <td className="py-2 sm:py-3 text-right text-xs sm:text-sm font-black pl-2">{fmt(item.quantity * item.price)} kr</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Summering */}
          <div className="flex justify-end mb-6">
            <div className="w-full sm:w-64 space-y-2">
              <div className="flex justify-between text-[10px] font-black uppercase text-slate-400">
                <span>Subtotal:</span><span>{fmt(quote.subtotal)} kr</span>
              </div>
              <div className={`flex justify-between text-[10px] font-black uppercase text-slate-400 ${!quote.use_rot ? 'border-b border-slate-100 pb-2' : ''}`}>
                <span>Moms (25%):</span><span>{fmt(quote.vat)} kr</span>
              </div>
              {quote.use_rot && parseFloat(quote.rot_deduction) > 0 && (
                <div className="flex justify-between text-blue-600 font-black text-sm uppercase border-b border-slate-100 pb-2">
                  <span>ROT-avdrag:</span><span>-{fmt(quote.rot_deduction)} kr</span>
                </div>
              )}
              <div className="flex justify-between font-black text-2xl border-t-4 border-slate-900 pt-3">
                <span>Totalt:</span><span>{fmt(quote.total)} kr</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-100 px-4 sm:px-8 py-3">
          <div className="flex flex-wrap gap-x-4 gap-y-1 justify-center sm:justify-between text-[9px] text-slate-400 font-black uppercase tracking-widest">
            <span>{quote.company_name}</span>
            {quote.org_nr && <span>Org: {quote.org_nr}</span>}
            {quote.phone && <span>{quote.phone}</span>}
            {quote.company_email && <span className="break-all lowercase">{quote.company_email}</span>}
          </div>
        </div>
      </div>

      {/* Svarssektion */}
      {alreadyAccepted ? (
        <div className="max-w-[700px] mx-auto rounded-2xl p-8 text-center border-2 bg-green-50 border-green-200">
          <CheckCircle className="mx-auto text-green-600 mb-3" size={40} />
          <h2 className="text-xl font-black text-green-800">Du har accepterat denna offert</h2>
          {quote.customer_response && quote.customer_response !== 'accepted' && (
            <p className="text-green-600 mt-2 italic">"{quote.customer_response}"</p>
          )}
          <p className="text-slate-400 text-sm mt-2">{quote.customer_responded_at && new Date(quote.customer_responded_at).toLocaleDateString('sv-SE')}</p>
        </div>
      ) : done && currentStatus === 'accepted' ? (
        <div className="max-w-[700px] mx-auto rounded-2xl p-8 text-center border-2 bg-green-50 border-green-200">
          <CheckCircle className="mx-auto text-green-600 mb-3" size={40} />
          <h2 className="text-xl font-black text-green-800">Offerten accepterad!</h2>
          <p className="text-green-600 mt-2">Tack! Leverantören har fått din bekräftelse.</p>
        </div>
      ) : done && currentStatus === 'declined' ? (
        <div className="max-w-[700px] mx-auto rounded-2xl p-8 text-center border-2 bg-red-50 border-red-200">
          <XCircle className="mx-auto text-red-500 mb-3" size={40} />
          <h2 className="text-xl font-black text-red-700">Offerten avböjd</h2>
          <p className="text-red-500 mt-2">Tack för ditt svar. Leverantören har informerats.</p>
        </div>
      ) : isExpired ? (
        <div className="max-w-[700px] mx-auto rounded-2xl p-6 text-center border-2 bg-amber-50 border-amber-200">
          <p className="font-black text-amber-700">Offerten har gått ut och kan inte längre besvaras.</p>
          <p className="text-slate-400 text-sm mt-1">Kontakta leverantören för en ny offert.</p>
        </div>
      ) : (
        <div className="max-w-[700px] mx-auto bg-white rounded-2xl border border-slate-200 p-6 space-y-5">
          <div>
            <h3 className="text-lg font-black mb-1 text-center">Vill du acceptera eller avböja offerten?</h3>
            <p className="text-slate-400 text-sm text-center">Ditt svar skickas direkt till leverantören.</p>
          </div>

          {/* Kommentarsfält */}
          <div>
            <label className="text-xs font-bold text-slate-500 uppercase flex items-center gap-1">
              Kommentar (valfritt)
              <Tooltip text="Din kommentar skickas med till leverantören tillsammans med ditt svar." />
            </label>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              className="w-full mt-2 p-3 border border-slate-200 rounded-xl text-sm resize-none outline-none focus:ring-2 focus:ring-blue-400"
              rows={3}
              placeholder="Skriv en kommentar eller fråga till leverantören..." />
          </div>

          {/* Bekräftelsesteg */}
          {pendingResponse ? (
            <div className={`rounded-xl p-4 border-2 ${pendingResponse === 'accepted' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <p className="font-black text-center mb-3">
                {pendingResponse === 'accepted' ? '✅ Bekräfta att du accepterar offerten' : '❌ Bekräfta att du avböjer offerten'}
              </p>
              {comment && <p className="text-sm italic text-slate-500 text-center mb-3">"{comment}"</p>}
              <div className="flex gap-3 justify-center">
                <button onClick={() => setPendingResponse(null)} className="px-5 py-2 border border-slate-300 rounded-xl text-sm font-bold text-slate-500">
                  Avbryt
                </button>
                <button onClick={() => handleRespond(pendingResponse)} disabled={responding}
                  className={`px-6 py-2 rounded-xl font-black text-white flex items-center gap-2 ${pendingResponse === 'accepted' ? 'bg-green-600' : 'bg-red-500'}`}>
                  {responding ? <Loader2 className="animate-spin" size={18} /> : null}
                  Ja, bekräfta
                </button>
              </div>
            </div>
          ) : (
            <div className="flex gap-4">
              <button onClick={() => setPendingResponse('declined')}
                className="flex-1 border-2 border-red-200 text-red-600 py-4 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-red-50">
                <XCircle size={20} /> Avböj
              </button>
              <button onClick={() => setPendingResponse('accepted')}
                className="flex-1 bg-green-600 text-white py-4 rounded-xl font-black flex items-center justify-center gap-2 shadow-lg hover:bg-green-700">
                <CheckCircle size={20} /> Acceptera
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CustomerPortal() {
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [tenantName, setTenantName] = useState('');
  const [quotes, setQuotes] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [directQuote, setDirectQuote] = useState(null);
  const [directLoading, setDirectLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const portalToken = params.get('token');
    if (portalToken) {
      setDirectLoading(true);
      fetch(`${API_URL}/portal/quotes/token/${portalToken}`)
        .then(r => r.json())
        .then(data => { if (!data.error) setDirectQuote(data); })
        .finally(() => setDirectLoading(false));
    }
  }, []);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/portal/request-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setStep('otp');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await fetch(`${API_URL}/portal/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setToken(data.token);
      setCustomerName(data.customer_name);
      setTenantName(data.tenant_name);
      const res2 = await fetch(`${API_URL}/portal/quotes`, { headers: { Authorization: `Bearer ${data.token}` } });
      const quotes = await res2.json();
      setQuotes(Array.isArray(quotes) ? quotes : []);
      setStep('quotes');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const STATUS = {
    sent: { label: 'Väntar på svar', cls: 'bg-blue-100 text-blue-700' },
    accepted: { label: 'Accepterad', cls: 'bg-green-100 text-green-700' },
    declined: { label: 'Avböjd', cls: 'bg-red-100 text-red-600' },
  };

  if (directLoading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <Loader2 className="animate-spin text-blue-600" size={40} />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-3">
          <FileText className="text-blue-600" size={22} />
          <span className="font-black text-xl">iOffer <span className="text-slate-400 font-normal text-base">– Offertportal</span></span>
        </div>
        {token && (
          <div className="text-right">
            <p className="font-black text-sm">{customerName}</p>
            <p className="text-xs text-slate-400">{tenantName}</p>
          </div>
        )}
      </header>

      <main className="max-w-3xl mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {directQuote ? (
          <QuoteView
            quote={directQuote}
            onRespond={(resp) => setDirectQuote({ ...directQuote, status: resp })}
          />
        ) : (step === 'email' || step === 'otp') ? (
          <div className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <FileText className="text-white" size={28} />
              </div>
              <h1 className="text-2xl font-black mb-2">Offertportalen</h1>
              <p className="text-slate-400">Logga in för att se och svara på offerter.</p>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-slate-100">
              {step === 'email' && (
                <form onSubmit={handleRequestOTP} className="space-y-4">
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase flex items-center">
                      E-postadress
                      <Tooltip text="Ange den e-postadress som leverantören registrerat dig med." />
                    </label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-3.5 text-slate-400" size={18} />
                      <input type="email" required value={email} onChange={e => setEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="din@epost.se" />
                    </div>
                  </div>
                  {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-xl">{error}</p>}
                  <button type="submit" disabled={loading}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-black flex items-center justify-center gap-2 hover:bg-blue-700">
                    {loading ? <Loader2 className="animate-spin" size={20} /> : <><span>Skicka inloggningskod</span><ArrowRight size={18} /></>}
                  </button>
                </form>
              )}
              {step === 'otp' && (
                <form onSubmit={handleVerifyOTP} className="space-y-4">
                  <div className="bg-blue-50 p-3 rounded-xl text-sm text-blue-700 font-medium">
                    En 6-siffrig kod skickades till <strong>{email}</strong>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-500 uppercase flex items-center">
                      Inloggningskod
                      <Tooltip text="Koden är giltig i 10 minuter. Begär en ny om den gått ut." />
                    </label>
                    <div className="relative mt-1">
                      <KeyRound className="absolute left-3 top-3.5 text-slate-400" size={18} />
                      <input type="text" inputMode="numeric" maxLength={6} required value={code}
                        onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                        className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 font-mono text-2xl tracking-widest text-center"
                        placeholder="000000" />
                    </div>
                  </div>
                  {error && <p className="text-red-600 text-sm bg-red-50 p-3 rounded-xl">{error}</p>}
                  <button type="submit" disabled={loading || code.length !== 6}
                    className="w-full bg-blue-600 text-white py-3 rounded-xl font-black disabled:opacity-50 hover:bg-blue-700">
                    {loading ? <Loader2 className="animate-spin mx-auto" size={20} /> : 'Logga in'}
                  </button>
                  <button type="button" onClick={() => { setStep('email'); setCode(''); setError(''); }}
                    className="w-full text-slate-400 text-sm py-2 hover:text-slate-600">← Tillbaka</button>
                </form>
              )}
            </div>
          </div>
        ) : step === 'quotes' && !selectedQuote ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-black">Mina offerter</h2>
              <p className="text-sm text-slate-400">{quotes.length} offert{quotes.length !== 1 ? 'er' : ''}</p>
            </div>
            {quotes.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                <FileText className="mx-auto text-slate-300 mb-3" size={40} />
                <p className="text-slate-400 font-bold">Inga offerter att visa ännu.</p>
              </div>
            ) : quotes.map(q => {
              const st = STATUS[q.status] || STATUS.sent;
              return (
                <div key={q.id} onClick={() => setSelectedQuote(q)}
                  className="bg-white rounded-2xl border border-slate-200 p-6 cursor-pointer hover:border-blue-400 hover:shadow-md transition-all">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1 flex-wrap">
                        <h3 className="font-black text-lg">{q.company_name}</h3>
                        <span className={`text-[10px] font-black uppercase px-2 py-1 rounded-full ${st.cls}`}>{st.label}</span>
                      </div>
                      {q.quote_number && <p className="text-xs text-slate-400 font-bold">Nr: {q.quote_number}</p>}
                      <p className="text-xs text-slate-400">{new Date(q.created_at).toLocaleDateString('sv-SE')}</p>
                      {q.customer_response && q.customer_response !== 'accepted' && q.customer_response !== 'declined' && (
                        <p className="text-xs text-slate-400 italic mt-1">"{q.customer_response}"</p>
                      )}
                    </div>
                    <p className="text-2xl font-black text-blue-600 ml-4">{fmt(q.total)} kr</p>
                  </div>
                </div>
              );
            })}
          </div>
        ) : step === 'quotes' && selectedQuote && (
          <QuoteView
            quote={selectedQuote}
            onBack={() => setSelectedQuote(null)}
            onRespond={(resp) => {
              setQuotes(prev => prev.map(q => q.id === selectedQuote.id ? { ...q, status: resp } : q));
              setSelectedQuote(prev => ({ ...prev, status: resp }));
            }}
          />
        )}
      </main>
    </div>
  );
}
